import { sleep } from '../utilities/general'
import { DRAWER_TABS, MENU_HEIGHT, PHONE_TAB_BAR_HEIGHT, Z_INDEX } from '../constants'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { type ReactNode, useState, useEffect } from 'react'
import { useTheme } from '@mui/material/styles'
import Paper, { PaperProps } from '@mui/material/Paper'
import useMediaQuery from '@mui/material/useMediaQuery'
import Box from '@mui/material/Box'
import Overflow from './Overflow'
import Stack from '@mui/material/Stack'
import Tabs from '@mui/material/Tabs'
import Menu from './Menu'
import useGraphView from '../models/useGraphView'
import Tab from '@mui/material/Tab'
import useDrawerResize from '../hooks/useDrawerResize'
import useOrganizations from '../models/useOrganizations'
import { listOfObjectPage } from '../utilities/orgSwitch'

const MENU_MARGIN_SMALL = 12
const MENU_MARGIN_LARGE = 26
const TRANSITION_DURATION = 200
const HANDLE_WIDTH = 12

const tabs = DRAWER_TABS

type DrawerProps = PaperProps & {
  children?: ReactNode
  /** The whole screen under the header, always open, no close button and no resize handle: the
   * noBGP app on a phone (Layout.tsx, lib/nativeApp.ts). */
  fullScreen?: boolean
}

export default function Drawer({ children, fullScreen = false, ...props }: Readonly<DrawerProps>) {
  const mapView = useGraphView(s => s.mapView)
  const theme = useTheme()
  const location = useLocation()
  const navigate = useNavigate()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [animateClose, setAnimateClose] = useState(false)
  const [lastLocation, setLastLocation] = useState(tabs[0].path)
  const menuMargin = isSmallScreen ? MENU_MARGIN_SMALL : MENU_MARGIN_LARGE
  const activeTabIndex = tabs.findIndex(tab => location.pathname.includes(tab.path))
  const open = fullScreen || (activeTabIndex > -1 && !animateClose)
  const {
    width: drawerWidth,
    isResizing,
    handleProps,
  } = useDrawerResize({ enabled: !isSmallScreen && !fullScreen, margin: menuMargin })

  const toggleOpen = async () => {
    if (open) {
      setAnimateClose(true)
      await sleep(TRANSITION_DURATION)
      setAnimateClose(false)
      navigate('/')
    } else {
      navigate(lastLocation)
    }
  }

  useEffect(() => {
    if (open) setLastLocation(location.pathname)
    // ⚠ A page that closed itself because its object is not in the active org (`useCloseWhenMissing`,
    // app#473) must not be the page the drawer opens again: the drawer button then opened it, it
    // closed again, and with the drawer closed no tab was in reach (app#475).
    else if ((location.state as { objectGone?: boolean } | null)?.objectGone)
      setLastLocation(last => listOfObjectPage(last) ?? last)
  }, [location.pathname, location.key])

  // ⚠ The page that the drawer opens again must not be a node or a network of the organization
  // that the reader left (app#471): with the drawer closed, a change of organization changes no
  // path, and the drawer then opened the old node's page as an empty pane. This covers every way
  // the organization changes, not only the selector in the header.
  const activeOrgId = useOrganizations(s => s.activeOrgId)
  useEffect(() => {
    // ⚠ One exception: the app changed the organization to FOLLOW the path of the page the drawer
    // shows (app#478). That page is then the right one to open again.
    // ⚠⚠ ASK THE STORE IF IT WAS A FOLLOW, NEVER `open` ALONE. After a pick in the header the
    // router's change of path arrives LATER than the store's change of organization (the router
    // updates in a transition), so this effect still sees the old object page as open. Keeping it
    // made the drawer button open the old page, and the follow rule then undid the reader's pick.
    const followed = useOrganizations.getState().followed === activeOrgId
    setLastLocation(last => (followed && open ? location.pathname : (listOfObjectPage(last) ?? last)))
  }, [activeOrgId])

  return (
    <Paper
      elevation={0}
      {...props}
      sx={({ shape, palette }) => ({
        position: 'absolute',
        display: 'flex',
        flexDirection: 'column',
        left: fullScreen ? 0 : menuMargin,
        top: MENU_HEIGHT,
        width: fullScreen ? '100%' : open ? drawerWidth : 68,
        // ⚠ dvh, not vh, on the phone, and the tab bar and the home indicator taken off: the
        // bottom of the list must not sit under either.
        height: fullScreen
          ? `calc(100dvh - ${MENU_HEIGHT + PHONE_TAB_BAR_HEIGHT}px - env(safe-area-inset-bottom))`
          : open
            ? `calc(100vh - ${MENU_HEIGHT + menuMargin}px)`
            : 48,
        transition: isResizing
          ? `height ${TRANSITION_DURATION}ms`
          : `height ${TRANSITION_DURATION}ms, width ${TRANSITION_DURATION}ms`,
        borderRadius: fullScreen ? 0 : shape.borderRadiusLarge + 'px',
        // ⚠ The closed drawer is only its button. On the map it has no box behind it, like the
        // logo (Benoit, 2026-10-02, nobgp/app#449). The open drawer keeps its surface: it holds text.
        background: !open && mapView ? 'transparent' : palette.transparent.light,
        backdropFilter: !open && mapView ? 'none' : shape.backdropBlur,
        overflow: 'hidden',
        zIndex: Z_INDEX.DRAWER,
      })}
    >
      <Stack direction="row">
        {!fullScreen && <Menu onClick={toggleOpen} />}
        <Tabs
          value={activeTabIndex !== -1 ? activeTabIndex : 0}
          orientation="horizontal"
          variant={fullScreen ? 'fullWidth' : 'standard'}
          sx={{ flex: fullScreen ? 1 : undefined }}
        >
          {tabs.map(tab => (
            <Tab key={tab.path} label={tab.label} component={Link} to={tab.path} />
          ))}
        </Tabs>
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Overflow sx={{ height: '100%', maxHeight: '100%' }}>{children}</Overflow>
      </Box>
      {open && !isSmallScreen && !fullScreen && (
        <Box
          {...handleProps}
          sx={({ palette }) => ({
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: HANDLE_WIDTH,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            cursor: 'ew-resize',
            touchAction: 'none',
            zIndex: 1,
            '&:hover > *, &:active > *': {
              background: palette.contrast.low,
            },
          })}
        >
          <Box
            sx={({ palette }) => ({
              width: 4,
              height: 50,
              borderRadius: 1.5,
              background: palette.contrast.minimal,
              transition: 'background 120ms',
            })}
          />
        </Box>
      )}
    </Paper>
  )
}
